import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import CloseIcon from '@mui/icons-material/Close';
import { fetchBulkProvisioningJob } from '../BulkProvisioningApi/bulkProvisioningApi.ts';
import { Counter } from 'common/components/Counter/Counter.tsx';
import { BulkProvisioningStatusChip } from './BulkProvisioningStatusChip.tsx';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Routes } from '../../../Routes.constants.ts';
import { useTranslation } from 'react-i18next';
import {
  failTestId,
  fileNameTestId,
  noOfCardsTestId,
  processedDateTestId,
  statusTestId,
  successTestId,
  uploadDateTestId,
} from '../BulkProvisioningDataTestIds.ts';
import { dialogPaper } from 'theme/utility/components.styles.ts';
import { usePreviousPath } from 'common/components/AppLayout/AppNavigation/usePreviousPath.ts';
import { toLocaleDateTime } from 'common/utils/dateUtils.ts';
import { ScrollArea } from 'common/components/ScrollArea/ScrollArea.tsx';

type BulkProvisioningJobRoutingParams = {
  id: string;
};

export const BulkProvisioningJobDetailsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<BulkProvisioningJobRoutingParams>();
  const { data: bulkProvisioningJob } = useSuspenseQuery({
    queryKey: ['BulkProvisioningJob', id],
    queryFn: ({ signal }) => (id ? fetchBulkProvisioningJob(id, signal) : null),
  });

  const {
    fileName,
    status,
    numberOfCards,
    numberOfSuccessCards,
    numberOfFailedCards,
    uploadAt,
    uploadFinishedAt,
  } = bulkProvisioningJob || {};

  const prevPath = usePreviousPath() || Routes.provisioningRequest;
  return (
    <Dialog open={!!id} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaper }}>
      <DialogTitle>
        {bulkProvisioningJob?.fileName}
        <IconButton
          aria-label={t('common.close')}
          component={Link}
          to={prevPath}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ScrollArea style={{ height: '100%' }}>
          <Grid container rowSpacing={4}>
            <Grid size={12}>
              <Grid container>
                <Grid size={3}>
                  <Typography variant="text4" color="grey.500">
                    {t('common.fileName')}:
                  </Typography>
                </Grid>
                <Grid data-testid={fileNameTestId}>{fileName}</Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container>
                <Grid size={3}>
                  <Typography variant="text4" color="grey.500">
                    {t('common.status')}:
                  </Typography>
                </Grid>
                <Grid data-testid={statusTestId}>
                  <BulkProvisioningStatusChip status={status} />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container>
                <Grid size={3}>
                  <Typography variant="text4" color="grey.500">
                    {t('common.numberOfCards')}:
                  </Typography>
                </Grid>
                <Grid data-testid={noOfCardsTestId}>
                  <Counter count={numberOfCards ?? 0} />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container>
                <Grid size={3}>
                  <Typography variant="text4" color="grey.500">
                    {t('common.success')}:
                  </Typography>
                </Grid>
                <Grid data-testid={successTestId}>
                  <Counter count={numberOfSuccessCards ?? 0} variant="success" />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container>
                <Grid size={3}>
                  <Typography variant="text4" color="grey.500">
                    {t('common.fail')}:
                  </Typography>
                </Grid>
                <Grid data-testid={failTestId}>
                  <Counter count={numberOfFailedCards ?? 0} variant="error" />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container>
                <Grid size={3}>
                  <Typography variant="text4" color="grey.500">
                    {t('common.uploadDate')}
                  </Typography>
                </Grid>
                <Grid data-testid={uploadDateTestId}>{toLocaleDateTime(uploadAt)}</Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container>
                <Grid size={3}>
                  <Typography variant="text4" color="grey.500">
                    {t('bulkProvisioning.processedDate')}:
                  </Typography>
                </Grid>
                <Grid data-testid={processedDateTestId}>{toLocaleDateTime(uploadFinishedAt)}</Grid>
              </Grid>
            </Grid>
          </Grid>
        </ScrollArea>
      </DialogContent>
      <DialogActions>
        <Button component={Link} to={prevPath}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
